import React from 'react';
import UICheckbox from '../UICheckbox';
import './UICheckboxWithLabeledFields.scss';

// Props for the entire component
export interface UICheckboxWithLabeledFieldsProps {
  // Checkbox props
  checkboxLabel: string;
  checked?: boolean;
  onCheckboxChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  checkboxVariant?: 'default' | 'success' | 'error';
  checkboxSize?: 'small' | 'medium' | 'large';
  checkboxDisabled?: boolean;

  // Input props
  inputLabel: string;
  inputValue?: string;
  onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputPlaceholder?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;

  // Textarea props
  textareaLabel: string;
  textareaValue?: string;
  onTextareaChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  textareaPlaceholder?: string;
  textareaProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;

  // Layout props
  labelWidth?: number; // Width of labels in percentage
  className?: string;
}

const UICheckboxWithLabeledFields: React.FC<
  UICheckboxWithLabeledFieldsProps
> = ({
  // Checkbox
  checkboxLabel,
  checked = false,
  onCheckboxChange,
  checkboxVariant = 'default',
  checkboxSize = 'medium',
  checkboxDisabled = false,

  // Input
  inputLabel,
  inputValue = '',
  onInputChange,
  inputPlaceholder,
  inputProps,

  // Textarea
  textareaLabel,
  textareaValue = '',
  onTextareaChange,
  textareaPlaceholder,
  textareaProps,

  // Layout
  labelWidth = 25, // Default label width
  className = '',
}) => {
  const labelStyle = {
    flex: `0 0 ${labelWidth}%`,
  };

  const fieldStyle = {
    flex: '1',
  };

  const renderLabel = (label: string) => {
    const parts = label.split('*');
    return parts.map((part, index) => (
      <React.Fragment key={index}>
        {part}
        {index < parts.length - 1 && (
          <span className="UICheckboxWithLabeledFields__asterisk">*</span>
        )}
      </React.Fragment>
    ));
  };

  return (
    <div className={`UICheckboxWithLabeledFields ${className}`}>
      {/* Ряд 1: Чекбокс */}
      <div className="UICheckboxWithLabeledFields__row">
        <UICheckbox
          customWidth={100} // Чекбокс займає всю ширину ряду
          label={checkboxLabel}
          checked={checked}
          onChange={onCheckboxChange}
          variant={checkboxVariant}
          size={checkboxSize}
          disabled={checkboxDisabled}
        />
      </div>

      {/* Ряд 2: Лейбл + Інпут */}
      <div className="UICheckboxWithLabeledFields__row">
        <label
          className="UICheckboxWithLabeledFields__label"
          style={labelStyle}
        >
          {renderLabel(inputLabel)}
        </label>
        <input
          className="UICheckboxWithLabeledFields__input"
          style={fieldStyle}
          value={inputValue}
          onChange={onInputChange}
          placeholder={inputPlaceholder}
          disabled={!checked || checkboxDisabled}
          {...inputProps}
        />
      </div>

      {/* Ряд 3: Лейбл + Textarea */}
      <div className="UICheckboxWithLabeledFields__row">
        <label
          className="UICheckboxWithLabeledFields__label"
          style={labelStyle}
        >
          {renderLabel(textareaLabel)}
        </label>
        <textarea
          className="UICheckboxWithLabeledFields__textarea"
          style={fieldStyle}
          value={textareaValue}
          onChange={onTextareaChange}
          placeholder={textareaPlaceholder}
          disabled={!checked || checkboxDisabled}
          {...textareaProps}
        />
      </div>
    </div>
  );
};

export default UICheckboxWithLabeledFields;
